import { notify, useModal } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { ModalId } from '../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { useForm, UseFormReturn } from '../../../../../hooks'
import { useCreateScoutConnectionImap } from '../hooks/useCreateScoutConnectionImap'
import { ScoutConnectionImapFormSchema } from '../types/scoutConnectionImapFormSchema'
import { getScoutConnectionImapFormSchema } from '../utils/getScoutConnectionImapFormSchema'

interface ScoutConnectionImapFormState {
  closeModal: () => void
  scoutConnectionImapForm: UseFormReturn<ScoutConnectionImapFormSchema>
  isProcessing: boolean
  submitForm: () => void
}

interface ScoutConnectionImapFormProviderProps {
  children: ReactNode
}

const ScoutConnectionImapFormContext = createContext<ScoutConnectionImapFormState | undefined>(undefined)

export const ScoutConnectionImapFormProvider = ({ children }: ScoutConnectionImapFormProviderProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeModal } = useModal(ModalId.ScoutConnectionImapModal)
  const queryClient = useQueryClient()
  const scoutConnectionImapForm = useForm<ScoutConnectionImapFormSchema>({
    defaultValues: {
      email: '',
      password: '',
      host: '',
      port: 993,
    },
    validationSchema: getScoutConnectionImapFormSchema(),
  })

  const { Form, handleSubmit, setError } = scoutConnectionImapForm

  const { create: createScoutConnectionImap, isProcessing } = useCreateScoutConnectionImap({
    onSuccess: async () => {
      closeModal()
      await queryClient.invalidateQueries(QueryKeys.ScoutConnections)
      notify({
        id: NotificationKeys.ScoutConnectionImapCreateSuccess,
        message: t('scout.connection.imap.modal.connect.success'),
        variant: 'success',
      })
    },
    onError: () => {
      setError('email', { message: '' })
      setError('password', { message: '' })
      setError('host', { message: '' })
      setError('port', { message: '' })
      setError('root.connectErrorMessage', { message: t('scout.connection.imap.modal.connect.error') })
    },
  })

  const submitForm = useCallback(() => {
    handleSubmit(createScoutConnectionImap)()
  }, [handleSubmit, createScoutConnectionImap])

  const providerValue = useMemo(
    () => ({
      closeModal,
      scoutConnectionImapForm,
      isProcessing,
      submitForm,
    }),
    [closeModal, scoutConnectionImapForm, isProcessing, submitForm],
  )

  return (
    <ScoutConnectionImapFormContext.Provider value={providerValue}>
      <Form>{children}</Form>
    </ScoutConnectionImapFormContext.Provider>
  )
}

export const useScoutConnectionImapForm = () => {
  const context = useContext(ScoutConnectionImapFormContext)
  if (!context) {
    throw new Error('ScoutConnectionImapFormContext missing!')
  }
  return context
}
